import { ChevronRightIcon } from '@chakra-ui/icons'
import {
  Button,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useClipboard,
  VStack,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineLink } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import { getFileURL, SocialUrlTransforms } from '../../../utils/mediaLibrary'
import useImageKitClient from '../../hooks/useImageKitClient'
import useGetFileUrlArguments from '../../hooks/useGetFileUrlArguments'
import useToast from '../../hooks/useToast'
import type {
  MediaLibrarySearchFileResponse,
  UserSessionResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'
import Hover from '../Hover'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import CopyEmbedURLModal from './CopyEmbedURLModal'

interface Props {
  file: MediaLibrarySearchFileResponse
  isHover: boolean
}

const CopyURLPopover = ({ file, isHover }: Props): React.ReactElement => {
  const { clientInfo } = useContext(PublicLinksInfoContext)

  const { t } = useTranslation()

  const { user } = useContext(UserContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [openEmbedModal, handleEmbedModal] = useState(false)
  const { updatedAt, fileType, mime, isPublished } = file
  const { request } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [transformationToCopy, setTransformationToCopy] = useState<Array<
    Record<string, string>
  > | null>(null)

  const [urlToCopy, setUrlToCopy] = useState<string>('')
  const copyUrl = useRef<boolean>(false)

  const imagekit = useImageKitClient()
  const toast = useToast()
  const { onCopy } = useClipboard(urlToCopy)

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  useEffect(() => {
    if (urlToCopy !== '' && copyUrl.current) {
      onCopy()
      toast({
        description: t('copied_url_to_clipboard', 'Copied URL to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      copyUrl.current = false
      setUrlToCopy('')
    }
  }, [urlToCopy])

  useEffect(() => {
    if (transformationToCopy !== null && copyUrl.current) {
      const toastRef = toast({
        description: t('generating_url_to_copy', 'Generating URL to copy'),
        status: 'info',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      if (toastRef) {
        void getFileURL({
          file,
          ...getFileUrlDynamicArguments,
          request,
          transformation: transformationToCopy,
        })
          .then(data => {
            if (data.includes('ik-s=')) {
              if (data.includes('ik-auth-version')) {
                const url = new URL(data)
                url.searchParams.delete('ik-auth-version')
                url.searchParams.delete('ik-s')
                url.searchParams.delete('ik-t')
                setUrlToCopy(url.toString())
              } else {
                setUrlToCopy(data)
              }
            } else {
              setUrlToCopy(
                imagekit.url({
                  src: data,
                  transformation: transformationToCopy,
                }),
              )
            }
            toast.close(toastRef)
          })
          .catch(() => {
            toast.update(toastRef, {
              description: t(
                'generating_url_to_copy_error',
                'There was an error in generating the URL',
              ),
              status: 'error',
            })
          })
      }
    }
  }, [transformationToCopy])

  const UrlMenuItems = [
    {
      name: 'LinkedIn',
      condition: fileType === 'image',
      items: [
        {
          name: 'Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.linkedinPost, focus: 'auto' },
            ])
          },
        },
        {
          name: 'Link Preview',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.linkedLinkPreview, focus: 'auto' },
            ])
          },
        },
      ],
    },
    {
      name: 'Facebook',
      condition: fileType === 'image',
      items: [
        {
          name: 'Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.facebookPost, focus: 'auto' },
            ])
          },
        },
        {
          name: 'Story',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.facebookStory, focus: 'auto' },
            ])
          },
        },
      ],
    },
    {
      name: 'Twitter',
      condition: fileType === 'image',
      items: [
        {
          name: 'Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.twitterPost, focus: 'auto' },
            ])
          },
        },
        {
          name: 'Link Preview',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.twitterLinkPreview, focus: 'auto' },
            ])
          },
        },
      ],
    },
    {
      name: 'Instagram',
      condition: fileType === 'image',
      items: [
        {
          name: 'Square Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.instagramSquarePost, focus: 'auto' },
            ])
          },
        },
        {
          name: 'Horizontal Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              {
                ...SocialUrlTransforms.instagramHorizontalPost,
                focus: 'auto',
              },
            ])
          },
        },
        {
          name: 'Vertical Post',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              {
                ...SocialUrlTransforms.instagramVerticalPost,
                focus: 'auto',
              },
            ])
          },
        },
        {
          name: 'Story',
          handleClick: () => {
            copyUrl.current = true
            setTransformationToCopy([
              { ...SocialUrlTransforms.instagramStory, focus: 'auto' },
            ])
          },
        },
      ],
    },
    {
      name: 'Copy original URL',
      condition: true,
      handleClick: () => {
        copyUrl.current = true
        setTransformationToCopy([])
      },
    },
    {
      name: 'Embed',
      condition: !isPublicLink && mime?.includes('video') && isPublished,
      handleClick: () => {
        handleEmbedModal(true)
      },
    },
  ]

  return (
    <Popover placement="top-end" isLazy trigger="hover" offset={[2, 4]}>
      <PopoverTrigger>
        <IconButton
          aria-label="Share"
          icon={<Icon as={AiOutlineLink} />}
          variant="link"
          marginX="0"
          paddingX="0"
          minW="min-content"
          visibility={isHover ? 'visible' : 'hidden'}
          size="lg"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="3xs"
          fontWeight="medium"
          onClick={e => void e.stopPropagation()}
          onDoubleClick={e => void e.stopPropagation()}
        >
          <PopoverArrow />
          <PopoverHeader
            width="full"
            fontWeight="semibold"
            color="appSlate"
            bgColor="#EDF2F7"
            borderBottomWidth="0"
          >
            {t('share', 'Share')}
          </PopoverHeader>
          <PopoverBody padding="0">
            <VStack spacing="1em" paddingY="0.5em">
              {UrlMenuItems.filter(x => x.condition).map((x,idx) =>
                x.items ? (
                  <Hover display="flex" w="full" key={idx}>
                    {isHoverCopyUrlSideMenu => (
                      <Menu
                        isOpen={isHoverCopyUrlSideMenu}
                        placement="right-start"
                        isLazy
                        gutter={-8}
                      >
                        <MenuButton
                          as={Button}
                          rightIcon={<ChevronRightIcon />}
                          variant="link"
                          color="appSlate"
                          fontWeight="medium"
                          width="full"
                          textAlign="left"
                          padding="6"
                          paddingY="0"
                        >
                          {x.name}
                        </MenuButton>
                        <MenuList>
                          {x.items.map(y => (
                            <MenuItem key={y.name} onClick={y.handleClick}>
                              {y.name}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    )}
                  </Hover>
                ) : (
                  <Button
                    variant="link"
                    color="appSlate"
                    _hover={{ textColor: '#0450d5' }}
                    fontWeight="medium"
                    width="full"
                    justifyContent="flex-start"
                    padding="6"
                    paddingY="0"
                    onClick={x.handleClick}
                  >
                    {x.name}
                  </Button>
                ),
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
        {openEmbedModal && (
          <CopyEmbedURLModal
            file={file}
            openEmbedModal={openEmbedModal}
            handleEmbedModal={handleEmbedModal}
          />
        )}
      </Portal>
    </Popover>
  )
}

export default CopyURLPopover
